import { Route, Routes, BrowserRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, lazy, Suspense, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";

import Navbar from "./components/Navbar";
import Cart from "./components/Cart";
import Wishlist from "./components/WishList";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";

import { startGuestSession } from "./components/Session";
import { checkIsAdmin } from "./redux/actions/user";
import { allPromotionBanner } from "./redux/actions/promo";
import { auth } from "./components/firebase_component";
import ProtectedRoute from "./components/ProtectedRoute";
import CategoryList from "./pages/Category";
import WhatsAppFloatingButton from "./components/FloatingWhatsappBtn";
import PromotionalBar from "./components/PromotionalBar";

// Lazy-loaded pages
const Home = lazy(() => import("./pages/Home"));
const Detail = lazy(() => import("./pages/Detail"));
const SignIn = lazy(() => import("./pages/SignIn"));
const SignUp = lazy(() => import("./pages/SignUp"));
const User = lazy(() => import("./pages/User"));
const AboutUs = lazy(() => import("./pages/About"));
const ContactUs = lazy(() => import("./pages/Contact"));
const Orders = lazy(() => import("./pages/MyOrder"));
const AllOrders = lazy(() => import("./pages/AllOrders"));
const AllProductLists = lazy(() => import("./pages/AllProductLists"));
const PrivacyPolicy = lazy(() => import("./pages/legalPages/PrivacyPolicy"));
const ReturnPolicy = lazy(() => import("./pages/legalPages/ReturnPolicy"));
const TermsAndConditions = lazy(
  () => import("./pages/legalPages/TermConditions"),
);
const CheckoutPage = lazy(() => import("./wrappers/CheckoutWrapper"));
const PromoCodeAdmin = lazy(() => import("./pages/PromoCode"));
const HowToOrder = lazy(() => import("./pages/HowToOrder"));
const PaymentHelp = lazy(() => import("./pages/PaymentHelp"));
const CategoryProductsPage = lazy(() => import("./pages/CategoryProducts"));
const NotFound = lazy(() => import("./pages/NotFound"));

function App() {
  const [localuser, setLocalUser] = useState(null);
  const { drawer } = useSelector((state) => state.drawer);
  const { wishlistDrawer } = useSelector((state) => state.wishlistDrawer);
  const { promoBanners } = useSelector((state) => state.promoCodes); // check promo bar
  const dispatch = useDispatch();

  // Ref to Navbar + PromoBar container
  const stickyRef = useRef(null);
  const [stickyHeight, setStickyHeight] = useState(0);

  useEffect(() => {
    dispatch(allPromotionBanner());
  }, [dispatch]);

  // Recompute sticky height whenever promoBanners change
  useEffect(() => {
    if (stickyRef.current) {
      setStickyHeight(stickyRef.current.offsetHeight);
    }
  }, [promoBanners]);

  useEffect(() => {
    if (localuser?.uid && !localuser.isGuest) {
      dispatch(checkIsAdmin(localuser.uid));
    }
  }, [localuser, dispatch]);

  const navbarHeight = 0; // px, adjust to match your Navbar
  const promoHeight = 20; // px, adjust to match your PromotionalBar

  // Compute total sticky height
  const totalSticky =
    promoBanners && promoBanners.length > 0
      ? navbarHeight + promoHeight
      : navbarHeight;

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;

      if (user) {
        setLocalUser({
          uid: user.uid,
          FullName: user.displayName || "Guest",
          Email: user.email || "",
          PhoneNumber: user.phoneNumber || "",
          isGuest: user.isAnonymous,
        });
      } else {
        const guest = await startGuestSession();
        if (!cancelled) {
          setLocalUser({
            uid: guest.uid,
            FullName: "Guest",
            Email: "",
            PhoneNumber: "",
            isGuest: true,
          });
        }
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <BrowserRouter>
        <ScrollToTop />
        {/* Navbar + PromoBar */}
        <div ref={stickyRef} className="sticky top-0 z-50 relative">
          {promoBanners && promoBanners.length > 0 && <PromotionalBar />}
          <Navbar />
        </div>

        {/* Main Content */}
        <main className="flex-grow">
          <div
            style={{
              paddingTop:
                window.innerWidth < 768
                  ? `${totalSticky + 40}px`
                  : `${totalSticky}px`,
            }}
          >
            <Suspense
              fallback={<div className="p-10 text-center">Loading...</div>}
            >
              {drawer && (
                <Cart
                  closeCart={() => dispatch({ type: "DRAWER", payload: false })}
                />
              )}
              {wishlistDrawer && <Wishlist />}
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="product/:slug" element={<Detail />} />
                <Route path="login" element={<SignIn />} />
                <Route path="signup" element={<SignUp />} />
                {localuser && <Route path="user" element={<User />} />}
                <Route path="/contactus" element={<ContactUs />} />
                <Route path="aboutus" element={<AboutUs />} />
                <Route path="my-orders" element={<Orders />} />
                <Route
                  path="all-orders"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AllOrders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="all-products"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AllProductLists />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="category"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <CategoryList />
                    </ProtectedRoute>
                  }
                />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/return-policy" element={<ReturnPolicy />} />
                <Route
                  path="/term-and-conditions"
                  element={<TermsAndConditions />}
                />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route
                  path="/promos"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <PromoCodeAdmin />
                    </ProtectedRoute>
                  }
                />
                <Route path="/howtoorder" element={<HowToOrder />} />
                <Route path="/paymenthelp" element={<PaymentHelp />} />
                <Route
                  path="/category/:slug"
                  element={<CategoryProductsPage />}
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
        </main>

        <WhatsAppFloatingButton />
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
